// Manager/Admin "Watering Schedules" report — the flat property inventory Blake
// asked for: one row per property, the municipal ALLOWED days in one column and
// what the controllers are actually PROGRAMMED to do in the next, with filters
// for branch, property group, city and class.
//
// Read-only over GET /api/dashboard/watering-schedules. Unlike ComplianceReport
// (which groups by city and only covers properties that HAVE a city rule), this
// lists every active property so the gaps — no city rule, no controller
// programming, no branch — are visible rows rather than silent omissions.
// Classification reuses the shared engine in lib/waterRestrictions.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Droplets, Printer, Search, X } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Card, Pill, StatTile, type Tone } from './dashboard/charts';
import {
  evaluatePropertyComplianceDetailed,
  formatDays,
  formatProgramLines,
  unionProgrammedDays,
  WATER_CLASS_LABELS,
  type DayAbbr,
  type WaterRestrictionSchedule,
  type ComplianceStatus,
  type ProgramLike,
  type WaterClass,
  type WaterRule,
} from '../lib/waterRestrictions';

// ---------------------------------------------------------------------------
// Endpoint shape (mirrors repositories/waterRestrictionSchedules.WateringScheduleProperty)
// ---------------------------------------------------------------------------
interface ApiController {
  id: string;
  controller_number: number;
  description: string | null;
  location: string | null;
  water_start_times: { programs?: ProgramLike[] } | null;
  water_restriction_override: { acknowledged?: boolean } | null;
}

interface ApiProperty {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  branch: string | null;
  branch_code: string | null;
  property_group: string | null;
  water_restriction_class: WaterClass | null;
  water_restriction_override: unknown;
  /** created_at of the most recent non-draft inspection report, or null. */
  last_inspected_at: string | null;
  controllers: ApiController[];
}

interface ApiResponse {
  properties: ApiProperty[];
  schedules: WaterRestrictionSchedule[];
}

/** A property resolved to its two schedule columns + a compliance verdict. */
interface Row {
  property: ApiProperty;
  allowedDays: DayAbbr[] | null;
  /** Union of every program's days across every controller on the property. */
  programmedDays: DayAbbr[];
  /**
   * Every program on every controller, rendered one line each. Built here rather
   * than during render: formatting runs Intl per start time, and the table
   * re-renders on every keystroke in the search box — recomputing ~300 rows of
   * this each time is wasted work. Rebuilt only when the data or language changes.
   */
  programBlocks: Array<{ controller: string | null; lines: string[] }>;
  /** True when no controller has any programmed schedule at all. */
  unprogrammed: boolean;
  status: ComplianceStatus;
  overridden: boolean;
}

const UNASSIGNED = '__unassigned__'; // sentinel filter value for a null branch/group/city

const chipClass = (active: boolean) =>
  `px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
    active
      ? 'bg-blue-600 text-white border-blue-600'
      : 'bg-surface text-theme-secondary border-line hover:bg-subtle'
  }`;

/**
 * One filter chip row. Module-level rather than nested in the report body so it
 * isn't redeclared (and its subtree remounted) on every render. `hasNull` adds
 * an "Unassigned" chip so properties missing the field stay reachable instead of
 * being unfilterable.
 */
function ChipRow({
  label,
  options,
  hasNull,
  value,
  onChange,
  allLabel,
  unassignedLabel,
}: {
  label: string;
  options: string[];
  hasNull: boolean;
  value: string | null;
  onChange: (v: string | null) => void;
  allLabel: string;
  unassignedLabel: string;
}) {
  if (options.length === 0 && !hasNull) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold text-theme-secondary mr-1">{label}</span>
      <button type="button" className={chipClass(value === null)} onClick={() => onChange(null)}>
        {allLabel}
      </button>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={chipClass(value === o)}
          onClick={() => onChange(value === o ? null : o)}
        >
          {o}
        </button>
      ))}
      {hasNull && (
        <button
          type="button"
          className={chipClass(value === UNASSIGNED)}
          onClick={() => onChange(value === UNASSIGNED ? null : UNASSIGNED)}
        >
          {unassignedLabel}
        </button>
      )}
    </div>
  );
}

export function WateringSchedulesReport() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [classFilter, setClassFilter] = useState<WaterClass | null>(null);
  const [statusFilter, setStatusFilter] = useState<ComplianceStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<ApiResponse>('/api/dashboard/watering-schedules');
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Resolve every property to its allowed + programmed columns.
  // -------------------------------------------------------------------------
  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const byCity = new Map<string, WaterRestrictionSchedule>();
    for (const s of data.schedules) byCity.set(s.city.toLowerCase(), s);

    return data.properties.map((p) => {
      const schedule = p.city ? byCity.get(p.city.toLowerCase()) ?? null : null;
      const detail = evaluatePropertyComplianceDetailed(schedule, {
        id: p.id,
        name: p.name,
        address: p.address,
        city: p.city,
        water_restriction_class: p.water_restriction_class,
        // Freeform jsonb on the wire; the engine narrows/validates it itself.
        water_restriction_override: (p.water_restriction_override ?? null) as WaterRule | null,
        controllers: p.controllers,
      });

      // Shared with the violation engine so both columns normalize day strings
      // the same way (see unionProgrammedDays).
      const { days: programmedDays, hasAnyProgram } = unionProgrammedDays(p.controllers);

      // Per-program detail. Controllers are labelled only when more than one of
      // them contributes programs, so the common single-controller property
      // stays uncluttered.
      const withPrograms = p.controllers
        .map((c) => ({ c, lines: formatProgramLines(c.water_start_times?.programs, i18n.language) }))
        .filter((x) => x.lines.length > 0);
      const multi = withPrograms.length > 1;
      const programBlocks = withPrograms.map(({ c, lines }) => ({
        controller: multi
          ? t('wateringSchedules.controllerN', 'Controller {{n}}', { n: c.controller_number })
          : null,
        lines,
      }));

      return {
        property: p,
        allowedDays: detail.allowedDays,
        programmedDays,
        programBlocks,
        unprogrammed: !hasAnyProgram,
        status: detail.status,
        overridden: detail.overridden,
      };
    });
  }, [data, i18n.language, t]);

  // -------------------------------------------------------------------------
  // Filter option lists (built from the full set so the chips stay stable as
  // other filters narrow the table).
  // -------------------------------------------------------------------------
  const optionsFor = (pick: (p: ApiProperty) => string | null) => {
    const vals = new Set<string>();
    let hasNull = false;
    for (const r of rows) {
      const v = (pick(r.property) ?? '').trim();
      if (v) vals.add(v);
      else hasNull = true;
    }
    return { values: [...vals].sort((a, b) => a.localeCompare(b)), hasNull };
  };
  const branches = useMemo(() => optionsFor((p) => p.branch), [rows]);
  const groups = useMemo(() => optionsFor((p) => p.property_group), [rows]);
  const cities = useMemo(() => optionsFor((p) => p.city), [rows]);

  const matches = (value: string | null, filter: string | null) => {
    if (!filter) return true;
    const v = (value ?? '').trim();
    return filter === UNASSIGNED ? !v : v === filter;
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const p = r.property;
      if (!matches(p.branch, branchFilter)) return false;
      if (!matches(p.property_group, groupFilter)) return false;
      if (!matches(p.city, cityFilter)) return false;
      if (classFilter && p.water_restriction_class !== classFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (q && !`${p.name} ${p.address ?? ''} ${p.branch ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, branchFilter, groupFilter, cityFilter, classFilter, statusFilter]);

  const anyFilter = !!(search || branchFilter || groupFilter || cityFilter || classFilter || statusFilter);
  function clearFilters() {
    setSearch('');
    setBranchFilter(null);
    setGroupFilter(null);
    setCityFilter(null);
    setClassFilter(null);
    setStatusFilter(null);
  }

  // -------------------------------------------------------------------------
  // Labels
  // -------------------------------------------------------------------------
  function statusLabel(s: ComplianceStatus): string {
    switch (s) {
      case 'compliant':
        return t('wateringSchedules.statusCompliant', 'Within schedule');
      case 'non_compliant':
        return t('wateringSchedules.statusNonCompliant', 'Outside schedule');
      case 'undetermined':
        return t('wateringSchedules.statusUndetermined', "Can't verify");
      case 'no_schedule':
        return t('wateringSchedules.statusNoSchedule', 'No schedule');
    }
  }
  const statusTone: Record<ComplianceStatus, Tone> = {
    compliant: 'emerald',
    non_compliant: 'red',
    undetermined: 'amber',
    no_schedule: 'gray',
  };
  const classLabel = (c: WaterClass) => t(`waterClass.${c}`, WATER_CLASS_LABELS[c]);

  /** The allowed-days cell: a real day list, "no restriction", or why we can't say. */
  function allowedText(r: Row): string {
    if (!r.property.city) return t('wateringSchedules.noCity', 'No city on file');
    if (r.allowedDays == null) {
      return r.status === 'no_schedule'
        ? t('wateringSchedules.noRule', 'No city rule')
        : t('wateringSchedules.notDetermined', 'Not determined');
    }
    if (r.allowedDays.length === 0) return t('wateringSchedules.noRestriction', 'No day restriction');
    return formatDays(r.allowedDays);
  }

  /**
   * Every program on every controller, one line each — "Turf: Mon, Fri @ 12:00 AM,
   * 8:00 PM (382m)". The column used to collapse all of this into a bare union of
   * days (just "Mon, Fri"), which hid the times, the durations and the fact that a
   * property runs several programs on different schedules. The union is still what
   * the Status verdict is computed from; it is no longer all a manager sees.
   *
   * Controllers are labelled only when the property has more than one contributing
   * programs, so the common single-controller case stays uncluttered.
   */
  /** Flat one-cell rendering of the per-program detail, for CSV. */
  function programmedText(r: Row): string {
    if (r.property.controllers.length === 0) return t('wateringSchedules.noControllers', 'No controllers');
    const blocks = r.programBlocks;
    if (blocks.length === 0) return t('wateringSchedules.notProgrammed', 'Not programmed');
    return blocks
      .map((b) => (b.controller ? `${b.controller} — ${b.lines.join('; ')}` : b.lines.join('; ')))
      .join(' | ');
  }

  /**
   * When someone was last on this property. Deliberately its own column rather
   * than an annotation on the programmed days: the schedule is master data edited
   * on Property -> Controllers and is NOT captured by an inspection, so labelling
   * it "from the 6/1 inspection" would assert something untrue. Side by side, the
   * pair answers the useful question — programming nobody has looked at lately.
   */
  function lastInspectedText(r: Row): string {
    const raw = r.property.last_inspected_at;
    if (!raw) return t('wateringSchedules.neverInspected', 'Never');
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return t('wateringSchedules.neverInspected', 'Never');
    return d.toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // -------------------------------------------------------------------------
  // CSV export of the filtered rows.
  // -------------------------------------------------------------------------
  function exportCsv() {
    const headers = [
      t('wateringSchedules.csvProperty', 'Property'),
      t('wateringSchedules.csvBranch', 'Branch'),
      t('wateringSchedules.csvGroup', 'Property group'),
      t('wateringSchedules.csvCity', 'City'),
      t('wateringSchedules.csvClass', 'Class'),
      t('wateringSchedules.csvAllowed', 'Allowed watering days'),
      t('wateringSchedules.csvProgrammed', 'Programmed days'),
      t('wateringSchedules.csvLastInspected', 'Last inspected'),
      t('wateringSchedules.csvControllers', 'Controllers'),
      t('wateringSchedules.csvStatus', 'Status'),
    ];
    const esc = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.map(esc).join(',')];
    for (const r of visible) {
      lines.push(
        [
          r.property.name,
          r.property.branch ?? '',
          r.property.property_group ?? '',
          r.property.city ?? '',
          r.property.water_restriction_class ? classLabel(r.property.water_restriction_class) : '',
          allowedText(r),
          programmedText(r),
          lastInspectedText(r),
          String(r.property.controllers.length),
          statusLabel(r.status),
        ]
          .map(esc)
          .join(','),
      );
    }
    // Leading BOM so Excel opens the UTF-8 correctly (matches the other reports).
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `watering-schedules-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-64 bg-subtle rounded" />
          <div className="h-40 bg-subtle rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border p-4 bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-800 dark:text-red-300">
          {t('wateringSchedules.loadError', 'Could not load the watering schedules report.')} {error}
        </div>
      </div>
    );
  }

  const counts = {
    total: visible.length,
    compliant: visible.filter((r) => r.status === 'compliant').length,
    outside: visible.filter((r) => r.status === 'non_compliant').length,
    gaps: visible.filter((r) => r.status === 'no_schedule' || r.unprogrammed).length,
  };

  const allLabel = t('wateringSchedules.all', 'All');
  const unassignedLabel = t('wateringSchedules.unassigned', 'Unassigned');
  const CLASSES: WaterClass[] = ['residential', 'non_residential'];
  const STATUSES: ComplianceStatus[] = ['compliant', 'non_compliant', 'undetermined', 'no_schedule'];

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            {t('wateringSchedules.title', 'Watering Schedules')}
          </h1>
          <p className="text-sm text-theme-secondary mt-0.5">
            {t(
              'wateringSchedules.subtitle',
              'Every active property with the days its city allows watering and the days its controllers are actually programmed to run.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-line bg-surface text-theme-primary hover:bg-subtle"
          >
            <Download size={16} /> {t('wateringSchedules.exportCsv', 'Export CSV')}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-line bg-surface text-theme-primary hover:bg-subtle"
          >
            <Printer size={16} /> {t('wateringSchedules.print', 'Print')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          icon={<Droplets size={18} />}
          tone="blue"
          label={t('wateringSchedules.tileProperties', 'Properties')}
          value={counts.total}
        />
        <StatTile
          icon={<Droplets size={18} />}
          tone="emerald"
          label={t('wateringSchedules.tileWithin', 'Within schedule')}
          value={counts.compliant}
        />
        <StatTile
          icon={<Droplets size={18} />}
          tone="red"
          label={t('wateringSchedules.tileOutside', 'Outside schedule')}
          value={counts.outside}
        />
        <StatTile
          icon={<Droplets size={18} />}
          tone="amber"
          label={t('wateringSchedules.tileGaps', 'Missing schedule data')}
          value={counts.gaps}
        />
      </div>

      <Card bodyClassName="p-4 space-y-3">
        <div className="flex items-center gap-2 print:hidden">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('wateringSchedules.searchPlaceholder', 'Search properties…')}
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-line bg-surface text-theme-primary text-sm"
            />
          </div>
          {anyFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-sm border border-line bg-surface text-theme-secondary hover:bg-subtle"
            >
              <X size={14} /> {t('wateringSchedules.clearFilters', 'Clear filters')}
            </button>
          )}
        </div>

        <div className="space-y-2 print:hidden">
          <ChipRow
            label={t('wateringSchedules.filterBranch', 'Branch')}
            options={branches.values}
            hasNull={branches.hasNull}
            value={branchFilter}
            onChange={setBranchFilter}
            allLabel={allLabel}
            unassignedLabel={unassignedLabel}
          />
          <ChipRow
            label={t('wateringSchedules.filterGroup', 'Group')}
            options={groups.values}
            hasNull={groups.hasNull}
            value={groupFilter}
            onChange={setGroupFilter}
            allLabel={allLabel}
            unassignedLabel={unassignedLabel}
          />
          <ChipRow
            label={t('wateringSchedules.filterCity', 'City')}
            options={cities.values}
            hasNull={cities.hasNull}
            value={cityFilter}
            onChange={setCityFilter}
            allLabel={allLabel}
            unassignedLabel={unassignedLabel}
          />
          <ChipRow
            label={t('wateringSchedules.filterClass', 'Class')}
            options={CLASSES.map(classLabel)}
            hasNull={false}
            value={classFilter ? classLabel(classFilter) : null}
            onChange={(v) => setClassFilter(CLASSES.find((c) => classLabel(c) === v) ?? null)}
            allLabel={allLabel}
            unassignedLabel={unassignedLabel}
          />
          <ChipRow
            label={t('wateringSchedules.filterStatus', 'Status')}
            options={STATUSES.map(statusLabel)}
            hasNull={false}
            value={statusFilter ? statusLabel(statusFilter) : null}
            onChange={(v) => setStatusFilter(STATUSES.find((s) => statusLabel(s) === v) ?? null)}
            allLabel={allLabel}
            unassignedLabel={unassignedLabel}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-theme-secondary border-b border-line">
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colProperty', 'Property')}</th>
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colBranch', 'Branch')}</th>
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colCity', 'City')}</th>
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colAllowed', 'Allowed days')}</th>
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colProgrammed', 'Programmed days')}</th>
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colLastInspected', 'Last inspected')}</th>
                <th className="py-2 pr-4 font-semibold">{t('wateringSchedules.colStatus', 'Status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {visible.map((r) => (
                <tr key={r.property.id} className="align-top">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-theme-primary">{r.property.name}</div>
                    {r.property.address && <div className="text-xs text-theme-secondary">{r.property.address}</div>}
                  </td>
                  <td className="py-2 pr-4 text-theme-secondary">
                    {r.property.branch ?? (
                      <span className="italic">{t('wateringSchedules.unassigned', 'Unassigned')}</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-theme-secondary">{r.property.city ?? '—'}</td>
                  <td className="py-2 pr-4 text-theme-primary">{allowedText(r)}</td>
                  <td className="py-2 pr-4 text-theme-primary">
                    {r.property.controllers.length === 0 ? (
                      <span className="text-theme-secondary">
                        {t('wateringSchedules.noControllers', 'No controllers')}
                      </span>
                    ) : r.programBlocks.length === 0 ? (
                      <span className="text-theme-secondary">
                        {t('wateringSchedules.notProgrammed', 'Not programmed')}
                      </span>
                    ) : (
                      <div className="space-y-1.5">
                        {r.programBlocks.map((b, i) => (
                          <div key={i}>
                            {b.controller && (
                              <div className="text-xs text-theme-secondary">{b.controller}</div>
                            )}
                            {b.lines.map((line, j) => (
                              <div key={j}>{line}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-theme-secondary whitespace-nowrap">{lastInspectedText(r)}</td>
                  <td className="py-2 pr-4">
                    <Pill tone={statusTone[r.status]}>{statusLabel(r.status)}</Pill>
                    {r.overridden && (
                      <span className="ml-1 text-xs text-theme-secondary">
                        {t('wateringSchedules.overridden', '(override acknowledged)')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-theme-secondary">
                    {t('wateringSchedules.empty', 'No properties match these filters.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-theme-secondary">
          {t('wateringSchedules.rowCount', '{{count}} properties shown', { count: visible.length })}
          {' · '}
          {new Date().toLocaleDateString(i18n.language)}
        </p>
      </Card>
    </div>
  );
}
